"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SnapBackResultType = void 0;
exports.snapEntitiesToClosestPlace = snapEntitiesToClosestPlace;
var core_1 = require("@inter-ikea-core/core");
var SnapBackResultType;
(function (SnapBackResultType) {
    SnapBackResultType[SnapBackResultType["VALID_SNAP_WITH_NO_MOVEMENT"] = 0] = "VALID_SNAP_WITH_NO_MOVEMENT";
    SnapBackResultType[SnapBackResultType["VALID_SNAP_WITH_MOVEMENT"] = 1] = "VALID_SNAP_WITH_MOVEMENT";
    SnapBackResultType[SnapBackResultType["INVALID_SNAP"] = 2] = "INVALID_SNAP";
    SnapBackResultType[SnapBackResultType["NO_SNAP_FOUND"] = 3] = "NO_SNAP_FOUND";
    SnapBackResultType[SnapBackResultType["HAS_NO_CONNECTOR"] = 4] = "HAS_NO_CONNECTOR";
})(SnapBackResultType || (exports.SnapBackResultType = SnapBackResultType = {}));
/**
 * When snapping back things, we accept that the entity has moved a little bit. This is the
 * minimum distance that we consider as movement.
 */
var MIN_DISTANCE_COUNTED_AS_MOVEMENT = 0.1;
/**
 * Snaps entities to the closest place in the scene. If no snap is found, the entity is attached to the scene. A valid snap
 * will be performed before an invalid snap. If multiple snaps are valid the one with the shortest movement will be performed
 * first. If multiple invalid snaps are found the one with the shortest movement will be performed first. Sort order:
 * 1. Valid snap with no movement
 * 2. Valid snap with movement
 * 3. Snap with failing validation
 * 4. No snap found or has no connector
 *
 * @param entities
 * @param sceneManager
 * @param validationService
 * @returns a result for each entity hierarchy that tells if it was snapped, if it was invalid, if it found no matching
 * snap or has no connector.
 */
function snapEntitiesToClosestPlace(entities, sceneManager, validationService) {
    var bestSnapForEachEntity = entities.map(function (entity) {
        return findClosestValidatedSnap(entity, sceneManager, validationService);
    });
    sortByValidAndDistance(bestSnapForEachEntity);
    var result = bestSnapForEachEntity.map(function (snap) {
        if (snap.validSnap) {
            var requiredMovement = snap.validSnap.movement.size;
            snap.validSnap.createAction().perform();
            if (requiredMovement < MIN_DISTANCE_COUNTED_AS_MOVEMENT) {
                return {
                    result: SnapBackResultType.VALID_SNAP_WITH_NO_MOVEMENT,
                    entity: snap.entity
                };
            }
            else {
                return {
                    result: SnapBackResultType.VALID_SNAP_WITH_MOVEMENT,
                    entity: snap.entity
                };
            }
        }
        else if (snap.invalidSnap) {
            // We need to snap to something even if we can't find a valid snap
            snap.invalidSnap.createAction().perform();
            return { result: SnapBackResultType.INVALID_SNAP, entity: snap.entity };
        }
        else {
            // Has no snap, just attach it to the scene
            core_1.HierarchyUtils.setParentMaintainTransform(snap.entity, sceneManager.scene);
            if (snap.hasConnector) {
                return { result: SnapBackResultType.NO_SNAP_FOUND, entity: snap.entity };
            }
            else {
                return { result: SnapBackResultType.HAS_NO_CONNECTOR, entity: snap.entity };
            }
        }
    });
    return result;
}
function findClosestValidatedSnap(entity, sceneManager, validationService) {
    // Find all possible snaps sorted by distance
    var candidates = core_1.SnapActionCandidateSortAndSelectStrategies.SORT_BY_DISTANCE.sortAndSelect(core_1.SnapUtils.matchingSnapActionCandidatesInScene(entity, sceneManager, {
        includeAllSubConnectors: true,
        includeInternalConnectors: true,
        includeHostAssemblyParts: false,
        includeAlignmentConnectorsOutsideActivationDistance: true
    }));
    if (candidates.length > 0) {
        // Take the first valid and ignore warnings (false as third param)
        var validatedCandidate = validationService.getFirstValidSnapAction(candidates, false);
        var validCandidate = validatedCandidate.snapActionCandidate;
        if (validCandidate != null) {
            return { validSnap: validCandidate, entity: entity, hasConnector: true };
        }
        else {
            return { invalidSnap: candidates[0], entity: entity, hasConnector: true };
        }
    }
    else {
        if (core_1.ConnectorUtils.snappingEntitiesArray(entity).length === 0) {
            return { entity: entity, hasConnector: false };
        }
        return { entity: entity, hasConnector: true };
    }
}
/**
 * Sort by valid with the shortest movement and then invalid with the shortest movement
 * @param bestSnapForEachEntity
 */
function sortByValidAndDistance(bestSnapForEachEntity) {
    bestSnapForEachEntity.sort(function (a, b) {
        // If a is valid and b is not, a should come first (return -1))
        if (a.validSnap && !b.validSnap) {
            return -1;
        }
        // If b is valid and a is not, b should come first (return 1)
        if (!a.validSnap && b.validSnap) {
            return 1;
        }
        // If both are valid, the one with the shortest movement should come first
        if (a.validSnap && b.validSnap) {
            return a.validSnap.movement.size - b.validSnap.movement.size;
        }
        // If we come this far, none of the candidates has a valid snap. Let's pick the best invalid one.
        // If a has an invalid snap and b does not, a should come first (return -1)
        if (a.invalidSnap && !b.invalidSnap) {
            return -1;
        }
        // If b has an invalid snap and a does not, b should come first (return 1)
        if (!a.invalidSnap && b.invalidSnap) {
            return 1;
        }
        // If both are invalid, the one with the shortest movement should come first
        if (a.invalidSnap && b.invalidSnap) {
            return a.invalidSnap.movement.size - b.invalidSnap.movement.size;
        }
        return 0;
    });
}
//# sourceMappingURL=SnapBackUtils.js.map